import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "utils";

/*
  Non-stock delta (see docs/frontend-conventions.md): the brand badge is a light
  tinted pill, so `default`/`secondary` use the brand ramps (not stock's solid
  `bg-primary`/`bg-secondary`, which render a heavy blue/gray pill and dark-navy
  text via `*-foreground`). The status/trend variants are colored via the
  `--color-badge-*` brand tokens in index.css. Re-apply this block on `shadcn diff`.
*/
const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary-25 text-primary [a&]:hover:bg-primary-50",
        secondary:
          "bg-secondary-50 text-secondary-700 [a&]:hover:bg-secondary-100",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        active: "bg-primary-25 text-primary-700",
        success: "bg-badge-success-background text-badge-success",
        error: "bg-badge-error-background text-badge-error",
        // system-warning on this background is 1.74:1; caution-800 clears AA.
        warning: "bg-icon-warning-background text-caution-800",
        trendUp: "bg-badge-traction-bg-up text-badge-traction-text-up",
        trendDown: "bg-badge-traction-bg-down text-badge-traction-text-down",
        trendNoChange:
          "bg-badge-traction-bg-no_change text-badge-traction-text-no_change",
        trendNew: "bg-badge-traction-bg-new text-badge-traction-text-new",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-testid="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants, type BadgeVariant };
